/**
 * sticky-add-to-cart.js
 *
 * Sticky bar with quantity stepper that syncs with the main product form.
 * Updates unit price and case price based on quantity.
 */

(function () {
  'use strict';

  function initStickyBar(bar) {
    var formId = bar.dataset.formId;
    var triggerId = bar.dataset.triggerId;
    var qtyId = bar.dataset.qtyId;
    var trigger = document.getElementById(triggerId);
    var atcBtn = bar.querySelector('[data-sticky-atc-btn]');
    var atcSpinner = bar.querySelector('[data-sticky-atc-btn] .loading-overlay__spinner');
    var stickyQtyInput = bar.querySelector('.sticky-bar__qty-input');
    var unitPriceEl = bar.querySelector('[data-sticky-unit-price]');
    var casePriceEl = bar.querySelector('[data-sticky-case-price]');
    var casePriceLabel = (casePriceEl && casePriceEl.dataset.priceLabel) || 'Case price';

    var casePriceCents = parseInt(bar.dataset.casePrice, 10) || 0;
    var sqFtPerCase = parseFloat(bar.dataset.sqFtPerCase) || 1;
    var discountMultiplier = parseFloat(bar.dataset.discountMultiplier) || 1;

    if (!trigger || !atcBtn) return;

    // ── Show/hide logic ────────────────────────────────────────────────
    var triggerWasSeen = false;

    var observer = new IntersectionObserver(function (entries) {
      var entry = entries[0];
      if (entry.isIntersecting) {
        triggerWasSeen = true;
        bar.classList.remove('is-visible');
        bar.setAttribute('aria-hidden', 'true');
        document.documentElement.classList.remove('sticky-bar-active');
      } else if (triggerWasSeen) {
        var rect = trigger.getBoundingClientRect();
        if (rect.bottom < 0) {
          bar.classList.add('is-visible');
          bar.setAttribute('aria-hidden', 'false');
          document.documentElement.classList.add('sticky-bar-active');
        } else {
          bar.classList.remove('is-visible');
          bar.setAttribute('aria-hidden', 'true');
          document.documentElement.classList.remove('sticky-bar-active');
        }
      }
    }, { threshold: 0 });

    observer.observe(trigger);

    // ── Price formatting ───────────────────────────────────────────────
    function formatMoney(cents) {
      var amount = (cents / 100).toFixed(2);
      return '$' + amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    function updatePrices(qty) {
      var discountedCasePrice = Math.round(casePriceCents * discountMultiplier);
      var totalCents = discountedCasePrice * qty;
      var unitCents = Math.round(discountedCasePrice / sqFtPerCase);
      if (unitPriceEl) unitPriceEl.textContent = formatMoney(unitCents) + ' / sq ft';
      if (casePriceEl) casePriceEl.textContent = casePriceLabel + ': ' + formatMoney(totalCents);
    }

    // ── Quantity stepper ───────────────────────────────────────────────
    var mainQtyInput = document.getElementById(qtyId);

    if (mainQtyInput && stickyQtyInput) {
      stickyQtyInput.value = mainQtyInput.value || 1;
      updatePrices(parseInt(stickyQtyInput.value, 10) || 1);

      // Sticky qty -> main qty (live as user types)
      function onStickyQtyChange() {
        var val = parseInt(stickyQtyInput.value, 10);
        if (!val || val < 1) val = 1;
        mainQtyInput.value = val;
        updatePrices(val);
        mainQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      stickyQtyInput.addEventListener('input', onStickyQtyChange);
      stickyQtyInput.addEventListener('change', function () {
        var val = Math.max(1, parseInt(stickyQtyInput.value, 10) || 1);
        stickyQtyInput.value = val;
        onStickyQtyChange();
      });

      // Main qty -> sticky qty
      mainQtyInput.addEventListener('change', function () {
        var val = Math.max(1, parseInt(mainQtyInput.value, 10) || 1);
        stickyQtyInput.value = val;
        updatePrices(val);
      });

      // Also listen for input events from main calculator
      mainQtyInput.addEventListener('input', function () {
        var val = Math.max(1, parseInt(mainQtyInput.value, 10) || 1);
        stickyQtyInput.value = val;
        updatePrices(val);
      });
    }

    // +/- buttons
    bar.querySelectorAll('.sticky-bar__qty-btn').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        if (!stickyQtyInput) return;
        var current = parseInt(stickyQtyInput.value, 10) || 1;
        var next = btn.dataset.action === 'plus' ? current + 1 : Math.max(1, current - 1);
        stickyQtyInput.value = next;
        if (mainQtyInput) {
          mainQtyInput.value = next;
          mainQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        updatePrices(next);
      });
    });

    // ── ATC: proxy click to main form ──────────────────────────────────
    atcBtn.addEventListener('click', function (e) {
      e.preventDefault();
      var submitBtn = document.querySelector(
        '#' + formId + ' [type="submit"][name="add"]'
      );
      if (submitBtn && !submitBtn.disabled && submitBtn.getAttribute('aria-disabled') !== 'true') {
        // Show spinner and disable button immediately for instant feedback
        atcBtn.disabled = true;
        atcBtn.setAttribute('aria-disabled', 'true');
        if (atcSpinner) atcSpinner.classList.remove('hidden');
        var label = atcBtn.querySelector('.sticky-bar__atc-label');
        if (label) label.style.opacity = '0';
        submitBtn.click();
      }
    });

    // ── Mirror main button disabled state ──────────────────────────────
    var origSubmit = document.querySelector(
      '#' + formId + ' [type="submit"][name="add"]'
    );

    if (origSubmit) {
      new MutationObserver(function () {
        var isDisabled = origSubmit.hasAttribute('disabled') || origSubmit.getAttribute('aria-disabled') === 'true';
        atcBtn.disabled = isDisabled;
        if (isDisabled) {
          atcBtn.setAttribute('aria-disabled', 'true');
        } else {
          atcBtn.removeAttribute('aria-disabled');
        }
      }).observe(origSubmit, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled'],
      });

      // Mirror spinner and restore label when done
      var origSpinner = origSubmit.querySelector('.loading-overlay__spinner');
      var stickyLabel = atcBtn.querySelector('.sticky-bar__atc-label');
      if (origSpinner && atcSpinner) {
        new MutationObserver(function () {
          var spinnerHidden = origSpinner.classList.contains('hidden');
          atcSpinner.classList.toggle('hidden', spinnerHidden);
          if (spinnerHidden && stickyLabel) stickyLabel.style.opacity = '';
        }).observe(origSpinner, {
          attributes: true,
          attributeFilter: ['class'],
        });
      }
    }
  }

  document.querySelectorAll('.sticky-bar[data-section-id]').forEach(initStickyBar);
})();
